import { humanizeBytes } from 'src/utils/number-humanize';
import { Context, Controller } from '@hotwired/stimulus';
import { emitter } from 'src/emitter';

type FileDirectUploadedEvent = {
  element: HTMLElement;
  value: string;
  url: string;
  file: {
    name: string;
    size: number;
    type: string;
  };
};

/**
 * Enable file previewing for a file type input.
 *
 * @example The HTML for a minimal example
 *
 * <div data-controller="file-preview" data-file-preview-initial-value="...">
 *   <label data-file-preview-target="label">
 *     <span>Upload a file</span>
 *   </label>
 *   <input type="file" class="sr-only" data-file-preview-target="input">
 * </div>
 */
export default class FilePreviewController extends Controller {
  static targets = ['label', 'input'];

  static values = {
    initialUrl: String,
  };

  declare private labelTarget: HTMLLabelElement;
  declare private inputTarget: HTMLInputElement;

  declare private hasInitialUrlValue: boolean;
  declare private initialUrlValue: string;

  private objectUrl: string | undefined;
  private emptyLabel: Node | undefined;
  private form: HTMLFormElement | undefined;

  constructor(context: Context) {
    super(context);

    this.onFileChanged = this.onFileChanged.bind(this);
    this.onFileDropped = this.onFileDropped.bind(this);
    this.onFileDragOver = this.onFileDragOver.bind(this);
    this.onFileDirectUploaded = this.onFileDirectUploaded.bind(this);
    this.onFileDragEnter = this.onFileDragEnter.bind(this);
    this.onFileDragLeave = this.onFileDragLeave.bind(this);
  }

  public connect(): void {
    this.form = this.element.closest('form')!;

    this.inputTarget.addEventListener('change', this.onFileChanged);

    this.labelTarget.addEventListener('dragover', this.onFileDragOver, false);
    this.labelTarget.addEventListener('dragenter', this.onFileDragEnter);
    this.labelTarget.addEventListener('dragleave', this.onFileDragLeave);
    this.labelTarget.addEventListener('drop', this.onFileDropped);

    const child = this.labelTarget.firstElementChild;
    if (child instanceof HTMLElement) {
      child.style.pointerEvents = 'none';
    }

    emitter.on('file-direct-upload:uploaded', this.onFileDirectUploaded);

    this.emptyLabel = this.labelTarget.cloneNode(true);

    if (this.hasInitialUrlValue) {
      this.objectUrl = this.initialUrlValue;
      this.fill();
    }
  }

  public disconnect(): void {
    this.inputTarget.removeEventListener('change', this.onFileChanged);

    this.labelTarget.removeEventListener('dragover', this.onFileDragOver);
    this.labelTarget.removeEventListener('dragenter', this.onFileDragEnter);
    this.labelTarget.removeEventListener('dragleave', this.onFileDragLeave);
    this.labelTarget.removeEventListener('drop', this.onFileDropped);

    const child = this.labelTarget.firstElementChild;
    if (child instanceof HTMLElement) {
      child.style.pointerEvents = '';
    }

    emitter.off('file-direct-upload:uploaded', this.onFileDirectUploaded);
  }

  public clear() {
    // Move the input
    this.element.appendChild(this.inputTarget);

    while (this.labelTarget.lastElementChild) {
      this.labelTarget.removeChild(this.labelTarget.lastElementChild);
    }

    const newLabel = this.emptyLabel!.cloneNode(true);

    while (newLabel.lastChild) {
      this.labelTarget.prepend(newLabel.lastChild);
    }

    // Replace the input with the already initialized one
    this.labelTarget
      .querySelector('input[type="file"]')
      ?.replaceWith(this.inputTarget);
  }

  public fill(): void {
    if (!this.objectUrl) {
      return;
    }

    // Clear label
    while (this.labelTarget.lastElementChild) {
      this.labelTarget.removeChild(this.labelTarget.lastElementChild);
    }

    const image = new Image();
    image.src = this.objectUrl;
    image.classList.add(
      'w-full',
      'h-full',
      'object-center',
      'object-cover',
      'hover:opacity-75',
      'group-hover:opacity-75',
    );

    this.labelTarget.append(image);
  }

  public reference(name: string, size: number, _sizeCheck: boolean): void {
    if (!this.objectUrl) {
      return;
    }

    // Clear label
    while (this.labelTarget.lastElementChild) {
      this.labelTarget.removeChild(this.labelTarget.lastElementChild);
    }

    const link = document.createElement('a');
    link.href = this.objectUrl;
    link.target = '__blank';
    link.classList.add(
      'w-full',
      'h-full',
      'hover:opacity-75',
      'group-hover:opacity-75',
    );

    const humanSize = humanizeBytes(size);
    link.textContent = `${name} (${humanSize})`;

    if (size > 1024 * 1024 * 25) {
      const warning = document.createElement('span');
      warning.classList.add(
        'text-red-500',
        'italic',
        'font-medium',
        'inline-block',
        'ml-2',
      );
      warning.textContent =
        'File is likely too large; enable JavaScript and use direct upload';
      link.append(warning);
    } else if (size < 8) {
      const warning = document.createElement('span');
      warning.classList.add(
        'text-red-500',
        'italic',
        'font-medium',
        'inline-block',
        'ml-2',
      );
      warning.textContent = 'File is empty';
      link.append(warning);
    }

    this.labelTarget.append(link);
  }

  private onFileChanged(): void {
    if (!this.inputTarget || !this.inputTarget.files) {
      return;
    }

    const file = this.inputTarget.files[0];
    this.clear();

    if (!file) {
      emitter.emit('file-preview:cleared', { element: this.inputTarget });
      return;
    }

    if (this.objectUrl && this.objectUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.objectUrl);
    }

    this.objectUrl = URL.createObjectURL(file);

    console.log('[file-preview] new object url', this.objectUrl);
    if (file.type.split('/')[0] === 'image') {
      this.fill();
    } else {
      this.reference(file.name, file.size, true);
    }

    emitter.emit('file-preview:changed', {
      element: this.inputTarget,
      file: file,
    });
  }

  private onFileDropped(event: DragEvent): void {
    this.labelTarget.removeAttribute('data-drag-target');

    if (!event.dataTransfer) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.clear();

    this.inputTarget.files = event.dataTransfer.files;
    this.onFileChanged();
  }

  private onFileDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  private onFileDragEnter(event: DragEvent): void {
    if (!event.dataTransfer) {
      return;
    }

    this.labelTarget.setAttribute('data-drag-target', 'active');
  }

  private onFileDragLeave(event: DragEvent): void {
    if (!event.dataTransfer) {
      return;
    }

    this.labelTarget.removeAttribute('data-drag-target');
  }

  private onFileDirectUploaded(params: unknown): void {
    console.log('[file-preview] onFileDirectUploaded', params);

    if (!params || typeof params !== 'object') {
      return;
    }

    const { url, file, element } = params as FileDirectUploadedEvent;

    if (
      element != this.element &&
      !this.element.contains(element) &&
      !element.contains(this.element)
    ) {
      return;
    }

    if (this.objectUrl && this.objectUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.objectUrl);
    }

    this.objectUrl = url;

    console.log('[file-preview] new direct upload url', url);
    if (file.type.split('/')[0] === 'image') {
      this.fill();
    } else {
      this.reference(file.name, file.size, false);
    }
  }
}
